import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../utils/api';
import { 
  formatDate, 
  formatTimeRange, 
  formatCurrency, 
  formatPercent,
  getMarginStatus,
  getStatusBadgeClass 
} from '../../utils/formatters';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  AlertCircle,
  TrendingUp,
  Users,
  ChevronRight
} from 'lucide-react';

// Parse date string as local date (not UTC) and format relative
function formatDateLocal(dateStr) {
  if (!dateStr) return '';
  const datePart = typeof dateStr === 'string' ? dateStr.substring(0, 10) : dateStr.toISOString().substring(0, 10);
  const [year, month, day] = datePart.split('-').map(Number);
  const gigDate = new Date(year, month - 1, day);
  
  // Get today as local date
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  const diffTime = gigDate.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  
  return gigDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function StatCard({ label, value, subValue, icon: Icon, status }) {
  const statusColors = {
    good: 'text-green-600',
    warning: 'text-yellow-600',
    bad: 'text-red-600',
    neutral: 'text-gray-900'
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-500">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${statusColors[status] || statusColors.neutral}`}>
              {value}
            </p>
            {subValue && (
              <p className="text-sm text-gray-500 mt-1">{subValue}</p>
            )}
          </div>
          {Icon && (
            <div className="p-2 bg-hub-50 rounded-lg">
              <Icon className="w-5 h-5 text-hub-600" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GigCard({ gig }) {
  return (
    <Link 
      to={`/admin/gigs/${gig.id}`}
      className="block card hover:shadow-md transition-shadow"
    >
      <div className="card-body">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <Calendar className="w-4 h-4 shrink-0" />
              <span>{formatDateLocal(gig.date)}</span>
              <span>•</span>
              <span>{formatTimeRange(gig.startTime, gig.endTime)}</span>
            </div>
            <h3 className="font-medium text-gray-900 truncate">
              {gig.venue} - {gig.location}
            </h3>
            <p className="text-sm text-gray-500 truncate">{gig.client}</p>
          </div>
          <span className={getStatusBadgeClass(gig.status)}>
            {gig.status}
          </span>
        </div>

        {gig.assignments && gig.assignments.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex flex-wrap gap-2">
              {gig.assignments.map((a) => (
                <div 
                  key={a.id}
                  className={`text-xs px-2 py-1 rounded-full ${
                    a.status === 'PENDING' 
                      ? 'bg-yellow-50 text-yellow-700' 
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {a.contractor} {a.role && `(${a.role})`}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Link>
  );
}

function PendingResponseCard({ assignment }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
      <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-gray-900 truncate">
          {assignment.contractor.name}
        </p>
        <p className="text-sm text-gray-500 truncate">
          {formatDateLocal(assignment.gig.date)} • {assignment.gig.location.venue.name}
        </p>
      </div>
      <Link 
        to={`/admin/gigs/${assignment.gig.id}`}
        className="btn-sm btn-secondary shrink-0"
      >
        View
      </Link>
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/dashboard/admin')
      .then(setData)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
        {error}
      </div>
    );
  }

  const { upcomingGigs, pendingResponses, currentMonth } = data;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <Link to="/admin/gigs/new" className="btn-primary">
          New Gig
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label={currentMonth.name}
          value={formatCurrency(currentMonth.totalInvoice)}
          subValue={`${currentMonth.gigCount} gigs`}
          icon={TrendingUp}
        />
        <StatCard
          label="Production Fee"
          value={formatCurrency(currentMonth.productionFee)}
          subValue={`${formatPercent(currentMonth.marginPercent)} margin`}
          icon={TrendingUp}
          status={getMarginStatus(currentMonth.marginPercent)}
        />
        <StatCard
          label="Pending Responses"
          value={pendingResponses.length}
          icon={AlertCircle}
          status={pendingResponses.length > 0 ? 'warning' : 'neutral'}
        />
        <StatCard
          label="Next 7 Days"
          value={`${upcomingGigs.length} gigs`}
          icon={Calendar}
        />
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upcoming gigs */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Upcoming Gigs</h2>
            <Link 
              to="/admin/schedule" 
              className="text-sm text-hub-600 hover:text-hub-700 flex items-center gap-1"
            >
              View all
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {upcomingGigs.length === 0 ? (
            <div className="card">
              <div className="card-body text-center py-8">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No gigs in the next 7 days</p>
                <Link to="/admin/gigs/new" className="btn-primary mt-4">
                  Create Gig
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingGigs.slice(0, 5).map((gig) => (
                <GigCard key={gig.id} gig={gig} />
              ))}
            </div>
          )}
        </div>

        {/* Pending responses */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Pending Responses
            {pendingResponses.length > 0 && (
              <span className="ml-2 text-sm font-normal text-yellow-600">
                ({pendingResponses.length})
              </span>
            )}
          </h2>

          {pendingResponses.length === 0 ? (
            <div className="card">
              <div className="card-body text-center py-8">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">All caught up!</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {pendingResponses.slice(0, 5).map((assignment) => (
                <PendingResponseCard key={assignment.id} assignment={assignment} />
              ))}
              {pendingResponses.length > 5 && (
                <p className="text-sm text-gray-500 text-center py-2">
                  +{pendingResponses.length - 5} more
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
